import React, { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import "./rewards.scss";
import { AuthContext } from "../../context/AuthContext";
import { DarkModeContext } from "../../context/darkModeContext";
import { getPointsHistory } from "../../services/pointsService";
// La fecha de cada movimiento se pinta siempre en hora de Colombia, la
// misma que usa el backend al registrarlo, y SOLO la fecha: la hora exacta
// del movimiento no aporta nada al colaborador. Ver utils/fechas.js.
import { formatearFecha } from "../../utils/fechas";


const Rewards = () => {
  const { currentUser, loading } = useContext(AuthContext || {});
  const { darkMode } = useContext(DarkModeContext) || { darkMode: false };
  const location = useLocation();
  const [points, setPoints] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyItems, setHistoryItems] = useState(null);

  useEffect(() => {
    if (loading) return;

    if (currentUser && currentUser.points !== undefined) {
      setPoints(currentUser.points);
      setStatusMessage("");
    } else if (currentUser) {
      setPoints(0);
      setStatusMessage("Los puntos se cargarán próximamente.");
    } else {
      setPoints(null);
      setStatusMessage("Inicia sesión para ver tus puntos.");
    }
  }, [currentUser, loading, location.pathname]);

  // Se resetea el historial ya cargado si cambia de usuario (por ejemplo,
  // al cerrar sesión y entrar con otra cuenta en la misma pestaña).
  useEffect(() => {
    setHistoryOpen(false);
    setHistoryItems(null);
    setHistoryError("");
  }, [currentUser?.id]);

  const toggleHistory = async () => {
    const nextOpen = !historyOpen;
    setHistoryOpen(nextOpen);
    if (nextOpen && historyItems === null && currentUser?.id) {
      setHistoryLoading(true);
      setHistoryError("");
      try {
        const data = await getPointsHistory(currentUser.id);
        setHistoryItems(Array.isArray(data) ? data : []);
      } catch (err) {
        setHistoryError(err.message || "No se pudo cargar el historial de movimientos.");
      } finally {
        setHistoryLoading(false);
      }
    }
  };

  return (
    <div className={`rewardsSection ${darkMode ? 'dark' : ''}`}>
      <div className="rewardsHeader">
        <h2>Billetera Yale</h2>
      </div>
      <div className="walletCard">
        <div className="pointsCircle">
          <span className="pointsValue">{points !== null ? points : "—"}</span>
          <span className="pointsLabel">PUNTOS</span>
        </div>
        <p className="syncMessage">{statusMessage}</p>
      </div>

      {currentUser && (
        <div className={`movementsHistory ${historyOpen ? "open" : ""}`}>
          <button
            type="button"
            className="movementsToggle"
            onClick={toggleHistory}
            aria-expanded={historyOpen}
          >
            <HistoryRoundedIcon fontSize="small" />
            <span>Historial de movimientos</span>
            <ExpandMoreRoundedIcon fontSize="small" className="chevron" />
          </button>

          {historyOpen && (
            <div className="movementsPanel">
              {historyLoading && <p className="movementsState">Cargando movimientos…</p>}

              {!historyLoading && historyError && (
                <p className="movementsState error">{historyError}</p>
              )}

              {!historyLoading && !historyError && historyItems && historyItems.length === 0 && (
                <p className="movementsState">Todavía no tienes movimientos de puntos registrados.</p>
              )}

              {!historyLoading && !historyError && historyItems && historyItems.length > 0 && (
                <ul className="movementsList">
                  {historyItems.map((item) => {
                    const isPositive = item.delta > 0;
                    return (
                      <li key={item.id} className={`movementItem ${isPositive ? "earned" : "spent"}`}>
                        <span className="movementIcon" aria-hidden="true">
                          {isPositive ? (
                            <ArrowUpwardRoundedIcon fontSize="small" />
                          ) : (
                            <ArrowDownwardRoundedIcon fontSize="small" />
                          )}
                        </span>
                        <span className="movementInfo">
                          {/* El motivo es el comentario que escribió RR.HH. en la
                              columna "Motivo / Comentario" del archivo de recarga:
                              es lo que le explica al empleado POR QUÉ cambiaron sus
                              puntos. */}
                          <span className="movementReason">{item.reason || "Movimiento de puntos"}</span>
                          <span className="movementDate">{formatearFecha(item.createdAt, "")}</span>
                          {/* Saldo antes y después del movimiento. El "antes" lo
                              calcula el backend como balanceAfter - delta. */}
                          {item.balanceAfter !== null && item.balanceAfter !== undefined && (
                            <span className="movementBalance">
                              <span className="balanceBefore">{item.balanceBefore ?? 0} pts</span>
                              <span className="balanceArrow" aria-hidden="true">→</span>
                              <span className="balanceAfter">{item.balanceAfter} pts</span>
                            </span>
                          )}
                        </span>
                        <span className="movementAmount">
                          {isPositive ? "+" : ""}
                          {item.delta} pts
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      <div className="offersSection">
        <h3>Ofertas activas</h3>
        <div className="noOffers">
          <div className="tagIcon" aria-hidden="true" />
          <p>No hay ofertas disponibles</p>
        </div>
      </div>
    </div>
  );
};

export default Rewards;
